import { getTranslatedText } from '@nile/components';
import { getApiMethodAndKeyNames, apiResolver, RMMClient } from '@nile/graphql';
import { GET_PATCH_CATEGORY_SEVERITY } from '@nile/rmm/src/modules/patch-management/Graphql/patch';
import { getPatchCategorySeverityOptions } from '@nile/rmm/src/modules/patch-management/services/patch-filter-helper.service';
import { assetClassName } from '@nile/rmm/src/modules/asset-management/components/constants/asset-rule-builder';
import { GET_WORKSTATION_LIST } from '@nile/rmm/src/modules/asset-management/Graphql/asset';

function getQuery(item) {
  // Check whole item name
  if (item.name === 'workStation.name') {
    return GET_WORKSTATION_LIST;
  }
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'category':
    case 'severity':
      return GET_PATCH_CATEGORY_SEVERITY;
    default:
      break;
  }
  return null;
}

/**
 * @description This method gets triggered on selecting a particular alert attribute
 * Responsible to return the accessKey based on the selected alert attribute
 * @return {string} - accessKey
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'workStation':
      return { key: 'getWorkStationList', nestedKey: 'assets' };
    case 'category':
    case 'patchDetail.category':
    case 'severity':
    case 'patchDetail.severity':
      return 'getPatchAllCategorySeverityMeta';
    default:
      break;
  }
}

function getPropValues(item, operator, allOperators, intl) {
  if (item?.name) {
    const query = apiResolver.Patch[item.name]?.api;
    const client = apiResolver.Patch[item.name]?.client;
    let variables = {};

    switch (item.name) {
      case 'client.name':
      case 'workStation.client.name':
      case 'workStation.site.name':
      case 'workStation.platformCategory':
      case 'workStation.platform':
        variables = {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        };
        break;
      default:
        break;
    }

    if (query && client) {
      return client.query({
        query: query,
        variables
      });
    }
    if (
      operator &&
      allOperators?.getRuleBuilderOperators?.operator_placeholders[operator]
    ) {
      return allOperators?.getRuleBuilderOperators?.operator_placeholders[
        operator
      ].map((el) => {
        return {
          ...el,
          label: getTranslatedText(intl, el.key),
          value: getTranslatedText(intl, el.key),
          name: el.key
        };
      });
    }
    return { client: RMMClient, query: getQuery(item) };
  }
  return {};
}

async function onPatchViewAttributeSelect(params) {
  const { item: field, operator, fields, allOperators, intl } = params;

  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl
  );
  // if async query is available then return the result
  if (allValues.client && allValues.query) {
    return allValues;
  }
  let res = [];
  const apiMethodAndKeyNames = getApiMethodAndKeyNames('Patch');

  if (selectedField?.name) {
    const method = apiMethodAndKeyNames[selectedField.name]?.method;
    const key = apiMethodAndKeyNames[selectedField.name]?.key;
    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[selectedField.name]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else {
      let keyToAccess = 'severities';
      if (
        selectedField?.name === 'category' ||
        selectedField?.name === 'patchDetail.category'
      ) {
        keyToAccess = 'categories';
      } else {
        keyToAccess = 'severities';
      }

      switch (selectedField.name) {
        case 'category':
        case 'severity':
        case 'patchDetail.severity':
        case 'patchDetail.category':
          res = allValues.data
            ? getPatchCategorySeverityOptions(
                allValues.data[method],
                keyToAccess
              )
            : [];
          break;
        case 'patchDetail.stabilitySeverity':
          res = [
            { name: 'STABLE', value: 'STABLE', label: 'Stable' },
            { name: 'NEUTRAL', value: 'NEUTRAL', label: 'Neutral' },
            { name: 'CAUTION', value: 'CAUTION', label: 'Caution' }
          ];
          break;
        case 'installationStatus':
          res = [
            {
              name: 'NewOrMissing',
              value: 'NewOrMissing',
              label: 'NewOrMissing'
            },
            {
              name: 'RebootRequired',
              value: 'RebootRequired',
              label: 'RebootRequired'
            },
            { name: 'Installed', value: 'Installed', label: 'Installed' },
            { name: 'Failed', value: 'Failed', label: 'Failed' }
          ];
          break;
        case 'workStation.platformCategory':
          res = [
            { name: 'SERVER', value: 'SERVER', label: 'Server' },
            { name: 'WORKSTATION', value: 'WORKSTATION', label: 'Workstation' }
          ];
          break;
        case 'workStation.status':
          res = [
            { name: 'ONLINE', value: 'ONLINE', label: 'Online' },
            { name: 'OFFLINE', value: 'OFFLINE', label: 'Offline' }
          ];
          break;
        case 'workStation.client.name':
        case 'workStation.site.name':
          const { key:accessKey   = '', nestedKey:accessNestedKey } = apiMethodAndKeyNames[
            selectedField.name
          ]
          res = allValues.data
            ? (accessNestedKey
                ? allValues.data[method][accessNestedKey]
                : allValues.data[method]
              ).map((el) => {
                return {
                  name: accessKey ? el[accessKey] : el,
                  value: accessKey ? el[accessKey] : el,
                  label: accessKey ? el[accessKey] : el
                };
              })
            : [];
          break;
        default:
          res = allValues.data
            ? allValues.data[method].map((el) => {
                return {
                  name: key ? el[key] : el,
                  value: key ? el[key] : el,
                  label: key ? el[key] : el
                };
              })
            : [];
      }
    }
  }

  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onPatchViewAttributeValueSearch = async (client, query, params) => {
  const attribute = params?.field?.split('.')[1];
  const nameKeyObj = getNameKey(params.field);
  const nameKey = nameKeyObj?.key || nameKeyObj;
  const nestedKey = nameKeyObj?.nestedKey || '';
  const res = await client.query({
    query: query,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: attribute || 'firstName',
              operator: 'startsWith',
              value: params.inputValue
            }
          ]
        }
      }
    }
  });
  const allOptions = res?.data?.[nameKey]
    ? (nestedKey ? res.data[nameKey][nestedKey] : res.data[nameKey]).map(
        (el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        }
      )
    : [];
  return allOptions;
};

export { onPatchViewAttributeSelect, onPatchViewAttributeValueSearch };
